import type {
  ActiveGraphOperation,
  ActiveGraphTarget,
  ActiveGraphToolInput,
  PresentationContent,
} from "../../types";
import {
  FACTSET_ACTIVE_GRAPH_CAPABILITIES,
  buildFactSetCatalogPromptBlock,
  findActiveGraphCapability,
} from "../../../shared/factset/chartCreatorRegistry";
import {
  ChartCreatorValidationError,
  assertChartParametersValid,
  normalizeActiveGraphChartParameters,
} from "../../../shared/factset/chartCreatorPayload";
import { chartCreatorFrequency } from "../../../shared/factset/chartCreatorFrequency";
import {
  getStylesetsViaApi,
  previewChartViaApi,
} from "../../../shared/factset/chartCreatorApi";
import {
  getStylesetsViaIntents,
  previewChartViaIntents,
  type AddActiveGraphCoords,
} from "../../../shared/factset/chartCreatorIntents";
import {
  resolveChartCreatorMode,
  runOrJoinChartCreatorReauth,
} from "../../../shared/factset/chartCreatorMode";
import type { FactsetChartParameters } from "../../../shared/factset/chartCreatorTypes";
import {
  applyActiveGraphTitleOverride,
  assertActiveGraphXml,
  normalizeActiveGraphTitle,
  parseActiveGraphSafeState,
  readActiveGraphTitle,
} from "../../../shared/factset/activeGraphXml";
import {
  lookupFactSetCompany,
  type CompanyLookupResult,
} from "../../../shared/factset/companyLookup";
import {
  addPowerPointActiveGraphViaIntents,
  getSelectedPowerPointActiveGraph,
  type ActiveGraphAddDispatchTracker,
} from "../factset/activeGraphIntents";
import {
  applyModificationsViaIntents,
  resolveSlideIndexByIdViaIntents,
} from "../factset/intentBridgeService";
import { extractPresentationContent } from "../slideService";
import {
  assertValidChartTarget,
  clampChartFrameUpdate,
  clampNewChartFrame,
} from "./chartFrameBounds";
import {
  isFactSetChartHelpersEnabled,
  type AgentToolContext,
  type ToolExecutionPolicy,
} from "./types";

export interface IdLookupToolInput {
  searchText: string;
}

export interface ActiveGraphToolResult {
  ok: boolean;
  code: string;
  stage: string;
  applied: boolean;
  partialMutation: boolean;
  retryable: boolean;
  fallbackAllowed: boolean;
  message?: string;
  warning?: string;
  data?: Record<string, unknown>;
  completionEvidence?: Record<string, unknown>;
}

function result(
  overrides: Partial<ActiveGraphToolResult> & Pick<ActiveGraphToolResult, "ok" | "code" | "stage">,
): ActiveGraphToolResult {
  return {
    applied: false,
    partialMutation: false,
    retryable: false,
    fallbackAllowed: false,
    ...overrides,
  };
}

function safeError(error: unknown): { code: string; message: string } {
  if (error instanceof ChartCreatorValidationError) {
    return { code: error.code, message: error.message };
  }
  if (error instanceof Error) {
    return {
      code:
        error.name === "ChartCreatorTimeoutError"
          ? "active_graph_preview_timeout"
          : error.name === "AbortError"
            ? "active_graph_cancelled"
            : "active_graph_failed",
      message: error.message,
    };
  }
  return { code: "active_graph_failed", message: String(error) };
}

function selectedSlideId(
  presentationContent: PresentationContent | undefined,
): string | undefined {
  if (!presentationContent) return undefined;
  const selected =
    presentationContent.slides.find(
      (slide) => slide.index === presentationContent.selectedSlideIndex,
    ) ?? presentationContent.slides[presentationContent.selectedSlideIndex];
  return selected?.id;
}

const ACTIVE_GRAPH_TARGET_RULES = {
  invalidCode: "active_graph_frame_invalid",
  label: "ActiveGraph",
  otherFields: ["slideId"],
};

const ACTIVE_GRAPH_DEFAULT_FRAME = { left: 60, top: 120, width: 480, height: 280 };

function assertValidTargetObject(
  target: ActiveGraphTarget | undefined,
): void {
  assertValidChartTarget(target, ACTIVE_GRAPH_TARGET_RULES);
}

function normalizeCoords(
  target: ActiveGraphTarget | undefined,
  bounds: AgentToolContext["slideBounds"],
): AddActiveGraphCoords {
  assertValidTargetObject(target);
  return clampNewChartFrame(target, bounds, ACTIVE_GRAPH_DEFAULT_FRAME);
}

function normalizeFrameUpdate(
  target: ActiveGraphTarget | undefined,
  selected: { left?: number; top?: number; width?: number; height?: number },
  bounds: AgentToolContext["slideBounds"],
): AddActiveGraphCoords | undefined {
  assertValidTargetObject(target);
  return clampChartFrameUpdate(target, selected, bounds, {
    code: "active_graph_frame_unavailable",
    message: "PowerPoint did not report enough selected-shape geometry for a safe frame update.",
  });
}

function createFingerprint(
  params: FactsetChartParameters,
  slideId: string,
  coords: AddActiveGraphCoords,
  titleOverride: string | undefined,
): string {
  const canonicalize = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(canonicalize);
    if (!value || typeof value !== "object") return value;
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, canonicalize(nested)]),
    );
  };
  return JSON.stringify(
    canonicalize({
      params,
      slideId,
      coords,
      titleOverride:
        titleOverride === undefined
          ? undefined
          : normalizeActiveGraphTitle(titleOverride),
    }),
  );
}

function mergeObservedPresentation(
  base: PresentationContent | undefined,
  observed: PresentationContent,
): PresentationContent {
  if (!base) return observed;
  const observedById = new Map(observed.slides.map((slide) => [slide.id, slide]));
  const slides = base.slides.map(
    (slide) => observedById.get(slide.id) ?? slide,
  );
  const knownIds = new Set(slides.map((slide) => slide.id));
  for (const slide of observed.slides) {
    if (!knownIds.has(slide.id)) slides.push(slide);
  }
  slides.sort((left, right) => left.index - right.index);
  return {
    ...base,
    slides,
    selectedSlideIndex: observed.selectedSlideIndex,
    slideWidth: observed.slideWidth ?? base.slideWidth,
    slideHeight: observed.slideHeight ?? base.slideHeight,
  };
}

/**
 * Whether the inserted `frq1` is the requested frequency, counting
 * ChartCreator's Q/CQ and A/CY aliases as the same value in either direction.
 */
function isRequestedFrequency(
  observed: string | undefined,
  chartType: string,
  requested: string,
): boolean {
  if (!observed) return false;
  const sent = (frequency: string) =>
    chartCreatorFrequency(chartType, frequency).toUpperCase();
  return sent(observed) === sent(requested);
}

async function previewForCurrentMode(
  params: FactsetChartParameters,
  options: { isEdit: boolean; signal?: AbortSignal },
) {
  const mode = await resolveChartCreatorMode();
  const preview = async () =>
    mode.mode === "api"
      ? previewChartViaApi(params, {
          isEdit: options.isEdit,
          generatePreview: "None",
          signal: options.signal,
        })
      : previewChartViaIntents(params, {
          isEdit: options.isEdit,
          generatePreview: "None",
          signal: options.signal,
        });
  try {
    return { mode: mode.mode, preview: await preview() };
  } catch (error) {
    if (error instanceof Error && error.name === "ChartCreatorReauthRequiredError") {
      await runOrJoinChartCreatorReauth();
      return { mode: "api" as const, preview: await previewChartViaApi(params, {
        isEdit: options.isEdit,
        generatePreview: "None",
        signal: options.signal,
      }) };
    }
    throw error;
  }
}

function isActiveGraphEnabled(policy: ToolExecutionPolicy | undefined): boolean {
  return policy?.allowActiveGraph === true;
}

function findUnexpectedOperationField(
  input: ActiveGraphToolInput,
): string | undefined {
  if (
    input.operation === "update_active_graph_frame" &&
    input.target?.slideId !== undefined
  ) {
    return "target.slideId";
  }
  const allowedFields: Record<ActiveGraphOperation, readonly (keyof ActiveGraphToolInput)[]> = {
    create_active_graph: ["operation", "chartParameters", "titleOverride", "target"],
    inspect_selected_active_graph: ["operation"],
    list_active_graph_stylesets: ["operation"],
    update_active_graph_frame: ["operation", "target"],
    delete_selected_active_graph: ["operation"],
  };
  const allowed = new Set(allowedFields[input.operation] ?? ["operation"]);
  return (Object.keys(input) as Array<keyof ActiveGraphToolInput>).find(
    (key) => input[key] !== undefined && !allowed.has(key),
  );
}

async function createActiveGraph(
  input: ActiveGraphToolInput,
  options: {
    presentationContent?: PresentationContent;
    policy?: ToolExecutionPolicy;
    context?: AgentToolContext;
  },
): Promise<ActiveGraphToolResult> {
  const gateEnabled = isActiveGraphEnabled(options.policy);
  if (!gateEnabled) {
    return result({
      ok: false,
      code: "active_graph_disabled",
      stage: "policy",
      message: "Live FactSet ActiveGraphs are not enabled for this request context.",
    });
  }
  if (!input.chartParameters) {
    return result({
      ok: false,
      code: "active_graph_parameters_required",
      stage: "validation",
      message: "create_active_graph requires chartParameters.",
    });
  }
  let params: FactsetChartParameters;
  let coords: AddActiveGraphCoords;
  try {
    params = normalizeActiveGraphChartParameters(input.chartParameters);
    assertChartParametersValid(params, "create_active_graph", undefined, true);
    coords = normalizeCoords(input.target, options.context?.slideBounds);
  } catch (error) {
    const safe = safeError(error);
    return result({
      ok: false,
      code: safe.code,
      stage: "validation",
      message: safe.message,
    });
  }
  const basePresentation =
    options.context?.latestPresentationContent ?? options.presentationContent;
  const explicitSlideId =
    typeof input.target?.slideId === "string"
      ? input.target.slideId.trim()
      : "";
  if (input.target?.slideId !== undefined && !explicitSlideId) {
    return result({
      ok: false,
      code: "active_graph_target_invalid",
      stage: "validation",
      message: "ActiveGraph target slideId must be a non-empty string.",
    });
  }
  const slideId = explicitSlideId || selectedSlideId(basePresentation);
  if (!slideId) {
    return result({
      ok: false,
      code: "active_graph_target_required",
      stage: "target",
      message: "A real target slide ID is required.",
    });
  }
  const fingerprint = createFingerprint(
    params,
    slideId,
    coords,
    input.titleOverride,
  );
  if (options.context && !options.context.activeGraphCreateResults) {
    options.context.activeGraphCreateResults = new Map();
  }
  const previous = options.context?.activeGraphCreateResults?.get(fingerprint);
  if (previous) return previous as ActiveGraphToolResult;

  let mode: "api" | "legacy";
  let preview: Awaited<ReturnType<typeof previewChartViaApi>>;
  try {
    const previewResult = await previewForCurrentMode(params, {
      isEdit: false,
      signal: options.context?.signal,
    });
    mode = previewResult.mode;
    preview = previewResult.preview;
    if (preview.error || !preview.fdsChartXml) {
      return result({
        ok: false,
        code: "active_graph_preview_failed",
        stage: "preview",
        message: preview.error || "Chart Creator returned no FDSChart XML.",
      });
    }
  } catch (error) {
    const safe = safeError(error);
    return result({
      ok: false,
      code: safe.code,
      stage: "preview",
      message: safe.message,
      retryable: safe.code !== "active_graph_cancelled",
    });
  }

  let xml = preview.fdsChartXml;
  let titleWarning: string | undefined;
  let expectedTitle: string | undefined;
  try {
    assertActiveGraphXml(xml, "Chart Creator preview XML");
    if (input.titleOverride) {
      const titled = applyActiveGraphTitleOverride(xml, input.titleOverride);
      xml = titled.xml;
      if (!titled.applied) {
        titleWarning = "The selected Chart Creator template did not expose a title override target.";
      } else {
        expectedTitle = normalizeActiveGraphTitle(input.titleOverride);
      }
    }
  } catch (error) {
    const safe = safeError(error);
    return result({
      ok: false,
      code: "active_graph_preview_invalid",
      stage: "preview",
      message: safe.message,
    });
  }
  if (options.context?.signal?.aborted) {
    return result({
      ok: false,
      code: "active_graph_cancelled",
      stage: "target",
      message: "ActiveGraph creation was cancelled before insertion.",
    });
  }

  let baseline: PresentationContent;
  try {
    baseline = await extractPresentationContent(undefined, {
      presentationRef: options.context?.presentationRef,
      filters: { slides: { ids: [slideId] } },
    });
  } catch (error) {
    const safe = safeError(error);
    return result({
      ok: false,
      code: "active_graph_target_observation_failed",
      stage: "target",
      message: safe.message,
      retryable: true,
    });
  }
  if (options.context?.signal?.aborted) {
    return result({
      ok: false,
      code: "active_graph_cancelled",
      stage: "target",
      message: "ActiveGraph creation was cancelled before insertion.",
    });
  }
  const baselineSlide = baseline.slides.find((slide) => slide.id === slideId);
  if (!baselineSlide) {
    return result({
      ok: false,
      code: "active_graph_target_not_observed",
      stage: "target",
      message: "The target slide must be observed immediately before inserting a live chart.",
      retryable: true,
    });
  }
  const beforeShapeIds = new Set(
    baselineSlide.shapes.map((shape) => shape.id),
  );

  let inserted: Awaited<ReturnType<typeof addPowerPointActiveGraphViaIntents>>;
  const addTracker: ActiveGraphAddDispatchTracker = {};
  try {
    inserted = await addPowerPointActiveGraphViaIntents(
      {
        fdsChartXml: xml,
        slideId,
        presentationRef: options.context?.presentationRef,
        coords,
        signal: options.context?.signal,
      },
      addTracker,
    );
  } catch (error) {
    const safe = safeError(error);
    const indeterminateCandidate =
      safe.code === "active_graph_cancelled" || /timed out/i.test(safe.message);
    // Pre-dispatch activate/select aborts never wrote, so they stay retryable
    // and must not poison the fingerprint cache or the turn guard. Any error
    // after dispatched === true (including post-insert cleanup such as
    // closeUndo()) may have mutated the deck, so it stays fail-closed to
    // indeterminate to avoid a retry-driven duplicate chart. Unknown (tracker
    // untouched, e.g. mocked) stays fail-closed to indeterminate for
    // cancel/timeout candidates.
    const partialMutation =
      addTracker.dispatched === true ||
      (indeterminateCandidate && addTracker.dispatched !== false);
    const output = result({
      ok: false,
      code: partialMutation ? "active_graph_insert_indeterminate" : safe.code,
      stage: "insert",
      partialMutation,
      message: safe.message,
      retryable: !partialMutation,
    });
    if (partialMutation && options.context) {
      options.context.activeGraphMutationIndeterminate = true;
    }
    if (partialMutation) {
      options.context?.activeGraphCreateResults?.set(fingerprint, output);
    }
    return output;
  }

  const selectedShape =
    inserted.selectedShapes.length === 1 &&
    inserted.selectedShapes[0].slideId === slideId
      ? inserted.selectedShapes[0]
      : undefined;
  let freshPresentation: PresentationContent | undefined;
  let extractedNewShapeIds: string[] = [];
  let uniqueNewShape:
    | PresentationContent["slides"][number]["shapes"][number]
    | undefined;
  try {
    freshPresentation = await extractPresentationContent(undefined, {
      presentationRef: options.context?.presentationRef,
      filters: { slides: { ids: [slideId] } },
    });
    const extractedNewShapes =
      freshPresentation.slides
        .find((slide) => slide.id === slideId)
        ?.shapes.filter((shape) => !beforeShapeIds.has(shape.id)) ?? [];
    extractedNewShapeIds = extractedNewShapes.map((shape) => shape.id);
    uniqueNewShape =
      extractedNewShapes.length === 1 ? extractedNewShapes[0] : undefined;
    if (options.context) {
      options.context.latestPresentationContent = mergeObservedPresentation(
        basePresentation,
        freshPresentation,
      );
    }
  } catch {
    freshPresentation = undefined;
  }
  const uniqueNewShapeDetected = uniqueNewShape !== undefined;
  const selectedShapeIsNew =
    selectedShape !== undefined && !beforeShapeIds.has(selectedShape.id);
  const selectedShapeIsUniqueNew =
    selectedShapeIsNew &&
    uniqueNewShapeDetected &&
    selectedShape?.id === uniqueNewShape?.id;
  let parametersConfirmed = false;
  if (inserted.selectedActiveGraphXml) {
    try {
      const insertedState = parseActiveGraphSafeState(
        inserted.selectedActiveGraphXml,
      );
      parametersConfirmed =
        insertedState.chartType === params.chartType &&
        insertedState.tickers
          .map((ticker) => ticker.toUpperCase())
          .join("\0") === params.tickers.join("\0") &&
        insertedState.startDate === params.startDate &&
        insertedState.endDate === params.endDate &&
        isRequestedFrequency(
          insertedState.frequency,
          params.chartType!,
          params.frequency!,
        ) &&
        (!expectedTitle ||
          readActiveGraphTitle(inserted.selectedActiveGraphXml) ===
            expectedTitle);
    } catch {
      parametersConfirmed = false;
    }
  }
  const xmlEvidence =
    inserted.selectedActiveGraphXmlConfirmed &&
    parametersConfirmed &&
    (selectedShapeIsUniqueNew ||
      (selectedShapeIsNew && freshPresentation === undefined));
  const confirmed = uniqueNewShapeDetected || xmlEvidence;
  const confirmedShapeId = uniqueNewShape?.id ?? selectedShape?.id;
  const output = result({
    ok: confirmed,
    code: confirmed
      ? "active_graph_created"
      : "active_graph_insert_unconfirmed",
    stage: "readback",
    applied: confirmed,
    partialMutation: !confirmed,
    message: confirmed
      ? "Live FactSet ActiveGraph created."
      : "PowerPoint acknowledged the insertion, but the new ActiveGraph could not be uniquely confirmed.",
    ...(titleWarning ? { warning: titleWarning } : {}),
    data: {
      chartType: params.chartType!,
      entityCount: params.tickers.length,
      slideId,
      ...(confirmedShapeId ? { shapeId: confirmedShapeId } : {}),
      extractedNewShapeIds,
      chartCreatorMode: mode,
      annotationValidationJson: preview.annotationsJson ?? null,
      stylesetChanged: preview.wasStylesetChanged ?? null,
    },
    completionEvidence: {
      previewSucceeded: true,
      insertAcknowledged: true,
      newOleDetected: uniqueNewShapeDetected || selectedShapeIsNew,
      selectedXmlConfirmed: inserted.selectedActiveGraphXmlConfirmed,
      parametersConfirmed,
      visualVerificationRequired: true,
    },
  });
  options.context?.activeGraphCreateResults?.set(fingerprint, output);
  if (!confirmed && options.context) {
    options.context.activeGraphMutationIndeterminate = true;
  }
  return output;
}

async function inspectSelected(
  context?: AgentToolContext,
): Promise<ActiveGraphToolResult> {
  try {
    const selected = await getSelectedPowerPointActiveGraph({
      signal: context?.signal,
      presentationRef: context?.presentationRef,
    });
    const state = parseActiveGraphSafeState(selected.fdsChartXml);
    if (context) context.activeGraphMutationIndeterminate = false;
    return result({
      ok: true,
      code: "active_graph_inspected",
      stage: "read",
      data: {
        shapeId: selected.shape.id,
        slideId: selected.shape.slideId,
        ...state,
      },
    });
  } catch (error) {
    return result({
      ok: false,
      code: "active_graph_selection_required",
      stage: "selection",
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

async function listStylesets(
  context?: AgentToolContext,
): Promise<ActiveGraphToolResult> {
  try {
    const mode = await resolveChartCreatorMode();
    let styles;
    try {
      styles =
        mode.mode === "api"
          ? await getStylesetsViaApi({ signal: context?.signal })
          : await getStylesetsViaIntents({ signal: context?.signal });
    } catch (error) {
      if (
        mode.mode !== "api" ||
        !(error instanceof Error) ||
        error.name !== "ChartCreatorReauthRequiredError"
      ) {
        throw error;
      }
      await runOrJoinChartCreatorReauth();
      styles = await getStylesetsViaApi({ signal: context?.signal });
    }
    return result({
      ok: true,
      code: "active_graph_stylesets_listed",
      stage: "read",
      data: styles as unknown as Record<string, unknown>,
    });
  } catch (error) {
    const safe = safeError(error);
    return result({
      ok: false,
      code: safe.code,
      stage: "read",
      message: safe.message,
      retryable: true,
    });
  }
}

async function mutateSelectedFrame(
  input: ActiveGraphToolInput,
  options: { context?: AgentToolContext; deleteShape: boolean },
): Promise<ActiveGraphToolResult> {
  let selectedRead = false;
  let mutationDispatched = false;
  try {
    const selected = await getSelectedPowerPointActiveGraph({
      signal: options.context?.signal,
      presentationRef: options.context?.presentationRef,
    });
    selectedRead = true;
    if (!selected.shape.slideId) {
      throw new Error("Selected ActiveGraph did not report its slide ID.");
    }
    const index = await resolveSlideIndexByIdViaIntents(
      selected.shape.slideId,
      options.context?.log,
      { presentationRef: options.context?.presentationRef },
    );
    if (typeof index !== "number" || !Number.isFinite(index)) {
      return result({
        ok: false,
        code: "active_graph_selection_required",
        stage: "selection",
        message:
          "The selected live chart's slide was not found in the current presentation. " +
          "Ask the user to single-click the chart and try again.",
        retryable: true,
      });
    }
    const action = options.deleteShape ? "delete" : "modify";
    const coords = options.deleteShape
      ? undefined
      : normalizeFrameUpdate(
          input.target,
          selected.shape,
          options.context?.slideBounds,
        );
    if (!options.deleteShape && !coords) {
      return result({
        ok: false,
        code: "active_graph_frame_required",
        stage: "validation",
        message: "Frame update requires at least one of left, top, width, or height.",
      });
    }
    mutationDispatched = true;
    const summary = await applyModificationsViaIntents(
      {
        slides: [{
          id: selected.shape.slideId,
          index,
          action: "modify",
          shapes: [{
            id: selected.shape.id,
            action,
            ...(!options.deleteShape && coords ? coords : {}),
          }],
        }],
      },
      undefined,
      { presentationRef: options.context?.presentationRef },
    );
    const applied = summary.errors.count === 0 && summary.applied.shapes > 0;
    const partialMutation = !applied && summary.applied.shapes > 0;
    if (partialMutation && options.context) {
      options.context.activeGraphMutationIndeterminate = true;
    }
    return result({
      ok: applied,
      code: applied
        ? options.deleteShape
          ? "active_graph_deleted"
          : "active_graph_frame_updated"
        : "active_graph_shape_mutation_failed",
      stage: "shape",
      applied,
      partialMutation,
      message: applied
        ? options.deleteShape
          ? "Selected live FactSet chart deleted."
          : "Selected live FactSet chart frame updated."
        : summary.errors.issues[0]?.message ??
          "PowerPoint did not confirm the requested live-chart shape mutation.",
      data: {
        shapeId: selected.shape.id,
        slideId: selected.shape.slideId,
      },
    });
  } catch (error) {
    const safe = safeError(error);
    const partialMutation = mutationDispatched;
    if (partialMutation && options.context) {
      options.context.activeGraphMutationIndeterminate = true;
    }
    return result({
      ok: false,
      code: partialMutation
        ? "active_graph_shape_mutation_indeterminate"
        : selectedRead
          ? safe.code
          : "active_graph_selection_required",
      stage: selectedRead ? "shape" : "selection",
      message: safe.message,
      partialMutation,
      retryable: !partialMutation,
    });
  }
}

export async function executeActiveGraphTool(
  input: ActiveGraphToolInput,
  options: {
    presentationContent?: PresentationContent;
    policy?: ToolExecutionPolicy;
    context?: AgentToolContext;
  } = {},
): Promise<ActiveGraphToolResult> {
  if (options.policy?.allowActiveGraph !== true) {
    return result({
      ok: false,
      code: "active_graph_disabled",
      stage: "policy",
      message: "Live FactSet ActiveGraphs are not enabled for this request context.",
    });
  }
  if (
    !input ||
    typeof input !== "object" ||
    Array.isArray(input) ||
    typeof (input as { operation?: unknown }).operation !== "string"
  ) {
    return result({
      ok: false,
      code: "active_graph_arguments_invalid",
      stage: "validation",
      message: "active_graph requires an operation payload.",
    });
  }
  const unexpectedField = findUnexpectedOperationField(input);
  if (unexpectedField) {
    return result({
      ok: false,
      code: "active_graph_arguments_invalid",
      stage: "validation",
      message: `${input.operation} does not accept "${unexpectedField}".`,
    });
  }
  if (
    options.context?.activeGraphMutationIndeterminate &&
    input.operation !== "inspect_selected_active_graph" &&
    input.operation !== "list_active_graph_stylesets"
  ) {
    return result({
      ok: false,
      code: "active_graph_observation_required",
      stage: "policy",
      message: "A prior ActiveGraph mutation is indeterminate; inspect the selected live chart before another write.",
    });
  }
  switch (input.operation) {
    case "create_active_graph":
      return createActiveGraph(input, options);
    case "inspect_selected_active_graph":
      return inspectSelected(options.context);
    case "list_active_graph_stylesets":
      return listStylesets(options.context);
    case "update_active_graph_frame":
      return mutateSelectedFrame(input, { context: options.context, deleteShape: false });
    case "delete_selected_active_graph":
      return mutateSelectedFrame(input, { context: options.context, deleteShape: true });
    default:
      return result({
        ok: false,
        code: "active_graph_operation_invalid",
        stage: "validation",
        message: `Unsupported ActiveGraph operation "${String(
          (input as { operation?: unknown }).operation,
        )}".`,
      });
  }
}

export function executeListActiveGraphTypes(
  policy: ToolExecutionPolicy | undefined,
): Record<string, unknown> {
  const enabled = isFactSetChartHelpersEnabled(policy);
  const entries = enabled ? [...FACTSET_ACTIVE_GRAPH_CAPABILITIES] : [];
  return {
    ok: enabled,
    total: entries.length,
    entries,
    promptCatalog: buildFactSetCatalogPromptBlock(enabled ? undefined : []),
  };
}

export async function executeActiveGraphIdLookup(
  input: IdLookupToolInput,
  context?: AgentToolContext,
  policy?: ToolExecutionPolicy,
): Promise<CompanyLookupResult> {
  const searchText =
    typeof input?.searchText === "string" ? input.searchText.trim() : "";
  if (!isFactSetChartHelpersEnabled(policy)) {
    return {
      success: false,
      searchText,
      candidates: [],
      error: "FactSet identifier lookup is not enabled for this request context.",
    };
  }
  if (!searchText) {
    return {
      success: false,
      searchText,
      candidates: [],
      error: "Company lookup requires a non-empty company name or identifier.",
    };
  }
  return lookupFactSetCompany(searchText, context?.signal);
}

export function isMutatingActiveGraphOperation(
  input: ActiveGraphToolInput | undefined,
): boolean {
  return Boolean(
    input &&
      input.operation !== "inspect_selected_active_graph" &&
      input.operation !== "list_active_graph_stylesets",
  );
}

export function activeGraphOperationLabel(input: ActiveGraphToolInput | undefined): string {
  const operation = input?.operation;
  if (operation === "create_active_graph") return "creating a live FactSet chart";
  if (operation === "delete_selected_active_graph") return "deleting the selected live chart";
  if (operation === "update_active_graph_frame") return "resizing the selected live chart";
  if (operation === "list_active_graph_stylesets") return "listing FactSet chart styles";
  return "inspecting a live FactSet chart";
}

export function resolveActiveGraphCapabilityName(chartType: string): string | undefined {
  return findActiveGraphCapability(chartType)?.displayName;
}
